<template>
	<div class="resource-usage">
		<div class="resource-usage__head">
			<h4 class="sgnl-h6-semibold sgnl-text-color-default">Resource Usage</h4>
			<div class="resource-metrics">
				<div class="resource-metric">
					<div class="resource-label sgnl-paragraph-s-medium sgnl-text-color-secondary">
						Concurrent Builds
					</div>
					<div class="resource-value sgnl-subtitle-medium sgnl-text-color-default">
						{{ formatConcurrentBuilds() }}
					</div>
				</div>
				<div class="resource-metric">
					<div class="resource-label sgnl-paragraph-s-medium sgnl-text-color-secondary">Concurrent Tasks</div>
					<div class="resource-value sgnl-subtitle-medium sgnl-text-color-default">
						{{ formatConcurrentTasks() }}
					</div>
				</div>
				<div class="resource-metric">
					<div class="resource-label sgnl-paragraph-s-medium sgnl-text-color-secondary">Cloud Storage</div>
					<div class="resource-value sgnl-subtitle-medium sgnl-text-color-default">
						{{ formatCloudStorage() }}
					</div>
				</div>
			</div>
		</div>
		<v-alert
			v-if="error"
			type="error"
			density="compact"
			variant="outlined"
		>
			{{ error }}
		</v-alert>
	</div>
</template>
<script lang="ts">
import { defineComponent, PropType } from "vue";
import { OrganizationResourceUsage } from "@/types/api/organizations";
import { mapState } from "pinia";
import { useUserStore } from "@/stores/user";

export default defineComponent({
	name: "OrganizationResourceUsage",
	props: {
		resourceUsage: {
			type: Object as PropType<OrganizationResourceUsage | null>,
			default: null,
		},
		error: {
			type: String,
			default: "",
		},
	},
	computed: {
		...mapState(useUserStore, {
			currentUserTierDetails: "currentUserTierDetails",
		}),
	},
	methods: {
		formatBytes(bytes: number): string {
			if (bytes === 0) return "0 Bytes";
			const k = 1000;
			const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
			const i = Math.floor(Math.log(bytes) / Math.log(k));
			return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
		},
		formatAgainstAllowance(
			current: number,
			limit: number | null | undefined,
			format: (value: number) => string,
		): string {
			if (limit === undefined) {
				return format(current);
			}

			return `${format(current)} / ${format(limit ?? 0)}`;
		},
		formatConcurrentBuilds(): string {
			return this.formatAgainstAllowance(
				this.resourceUsage?.ConcurrentBuildCount ?? 0,
				this.currentUserTierDetails?.allowance?.ConcurrentBuildCount,
				String,
			);
		},
		formatConcurrentTasks(): string {
			return this.formatAgainstAllowance(
				this.resourceUsage?.ConcurrentTaskCount ?? 0,
				this.currentUserTierDetails?.allowance?.ConcurrentTaskCount,
				String,
			);
		},
		formatCloudStorage(): string {
			return this.formatAgainstAllowance(
				this.resourceUsage?.CloudStorageBytes ?? 0,
				this.currentUserTierDetails?.allowance?.CloudStorageBytes,
				this.formatBytes,
			);
		},
	},
});
</script>

<style lang="scss" scoped>
.resource-usage {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-12);
}

.resource-usage__head {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-8);
}

.resource-metrics {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-12);
}

.resource-metric {
	display: flex;
	flex-direction: column;
	gap: var(--sgnl-space-4);
}

@media (min-width: 960px) {
	.resource-metrics {
		flex-direction: row;
	}

	.resource-metric {
		flex: 1;
	}
}
</style>
